import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FmAssetImageRenderer } from './FmAssetImageRenderer';
import { FmButton } from './FmButton';
import { useFmMotion } from './FmMotionHelper';
import { useLanguage } from '@/i18n';
import { getMediaAsset, type FmMediaAsset } from '@/shared/config/mediaRegistry';

function requirePoster(asset: FmMediaAsset): FmMediaAsset {
  if (!asset.poster) {
    throw new Error(
      `Media loop "${asset.key}" has no registered poster; a loop needs one for reduced motion and before playback.`,
    );
  }
  return getMediaAsset(asset.poster);
}

interface FmAssetLoopRendererProps {
  asset: FmMediaAsset;
  alt: string;
  onLoad?: () => void;
  onError?: () => void;
}

/**
 * Plays a registered short loop (the rendition FmAssetImage chose for the
 * viewport) muted inside an FmAssetImage frame. Reduced
 * motion shows the registered poster and never mounts a video; otherwise the
 * loop loads only near the viewport, plays while at least a third is visible,
 * and carries a pause control because it runs longer than five seconds
 * (WCAG 2.2 SC 2.2.2 Pause, Stop, Hide).
 */
export const FmAssetLoopRenderer: React.FC<FmAssetLoopRendererProps> = ({
  asset,
  alt,
  onLoad,
  onError,
}) => {
  const { t } = useLanguage();
  const { prefersReducedMotion } = useFmMotion();
  const poster = requirePoster(asset);
  const videoRef = useRef<HTMLVideoElement>(null);
  const loadedSrcRef = useRef<string | null>(null);
  const [nearViewport, setNearViewport] = useState(false);
  const [inView, setInView] = useState(false);
  const [manualPause, setManualPause] = useState(false);
  const [playing, setPlaying] = useState(false);

  const play = useCallback(
    (video: HTMLVideoElement) => {
      video.play().catch((error: unknown) => {
        // A pause or source swap interrupts a pending play(); only other refusals are failures.
        if (error instanceof DOMException && error.name === 'AbortError') return;
        onError?.();
      });
    },
    [onError],
  );

  const playWhenEligible = useCallback(() => {
    const video = videoRef.current;
    if (!video || !video.currentSrc || !inView || manualPause || asset.autoplay !== true) return;
    play(video);
  }, [asset.autoplay, inView, manualPause, play]);

  useEffect(playWhenEligible, [playWhenEligible]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;
    // Loading starts a quarter screen early; playback follows actual visibility only.
    const preload = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) setNearViewport(true);
      },
      { rootMargin: '25% 0%' },
    );
    const visibility = new IntersectionObserver(
      (entries) => {
        const visible = entries[entries.length - 1].intersectionRatio >= 0.33;
        setInView(visible);
        if (!visible && !video.paused) video.pause();
      },
      { threshold: [0, 0.33] },
    );
    preload.observe(video);
    visibility.observe(video);
    return () => {
      preload.disconnect();
      visibility.disconnect();
    };
  }, [prefersReducedMotion]);

  if (prefersReducedMotion) {
    return <FmAssetImageRenderer asset={poster} alt={alt} onLoad={onLoad} onError={onError} />;
  }

  const toggleLabel = playing ? t.common.ui.media.pauseMotion : t.common.ui.media.playMotion;
  return (
    <>
      <video
        ref={videoRef}
        className="fm-asset-image__img fm-asset-image__loop"
        data-ready="true"
        muted
        loop={asset.loop === true}
        playsInline
        poster={poster.src}
        preload={nearViewport ? 'auto' : 'none'}
        src={nearViewport ? asset.src : undefined}
        aria-label={alt || undefined}
        aria-hidden={alt ? undefined : 'true'}
        onCanPlay={() => {
          if (loadedSrcRef.current !== asset.src) {
            loadedSrcRef.current = asset.src;
            onLoad?.();
          }
          playWhenEligible();
        }}
        onError={onError}
        onPause={() => setPlaying(false)}
        onPlay={() => setPlaying(true)}
      />
      <FmButton
        variant="text"
        className="fm-asset-image__motion-toggle"
        aria-label={toggleLabel}
        title={toggleLabel}
        onClick={() => {
          const video = videoRef.current;
          if (!video) return;
          if (video.paused) {
            setManualPause(false);
            if (video.currentSrc) play(video);
          } else {
            setManualPause(true);
            video.pause();
          }
        }}
      >
        <span aria-hidden="true">{playing ? 'Ⅱ' : '▶'}</span>
      </FmButton>
    </>
  );
};

FmAssetLoopRenderer.displayName = 'FmAssetLoopRenderer';
